import { Component, Element, h, Host, Prop, State } from "@stencil/core";
import { debounce } from "../../utils/debounce";

const baseClass = "ux-scrollable-container";

@Component({
    tag: "ux-scrollable-container",
    styleUrl: "scrollable-container.scss",
    shadow: false,
})
export class UxScrollableContainer {
    @Element() host: HTMLUxScrollableContainerElement;
    /** Set scrollbar placement */
    @Prop() scrollbarPlacement: "top" | "bottom" | "top-bottom" = "top";

    /** width of the scrollbar: 0 to 1 */
    @State() width: number = 0;
    /** scroll of the scrollbar: 0 to 1 */
    @State() scroll: number = 0;

    /** active grabbing */
    private isDragging: boolean = false;

    private scrollElement: HTMLElement;
    private hostWidth: number = 0;
    private scrollWidth: number = 0;

    private eventMouseMove;
    private eventMouseUp;

    componentDidLoad() {
        const scrollTarget = this.host.querySelector<HTMLElement>(
            "[data-scroll-target]"
        );
        this.scrollElement =
            scrollTarget !== null
                ? scrollTarget
                : this.host.querySelector(`.${baseClass}__scroll`);
        this.updateSizes();
        this.addScrollListener();
        this.addResizeListener();
        this.observeMutations();
    }

    private updateSizes() {
        this.hostWidth = this.host.clientWidth;
        this.scrollWidth = this.scrollElement.scrollWidth;
        this.width = this.hostWidth / this.scrollWidth;
    }

    private updateSizesDebounced = debounce(() => {
        this.updateSizes();
    }, 100);

    private observeMutations() {
        const targetNode = this.scrollElement;
        // Options for the observer (which mutations to observe)
        const config = { attributes: true, childList: true, subtree: true };
        // Callback function to execute when mutations are observed
        const callback = (mutationList) => {
            for (const mutation of mutationList) {
                console.log(mutation.type);
                this.updateSizesDebounced();
            }
        };
        // Create an observer instance linked to the callback function
        const observer = new MutationObserver(callback);
        // Start observing the target node for configured mutations
        observer.observe(targetNode, config);
    }

    private addResizeListener = debounce(() => {
        window.addEventListener("resize", this.updateSizes.bind(this));
    }, 100);

    private addScrollListener = debounce(() => {
        this.scrollElement.addEventListener(
            "scroll",
            () => {
                const setScroll = () => {
                    this.scroll =
                        this.scrollElement.scrollLeft / this.scrollWidth;
                };

                requestAnimationFrame(() => {
                    setScroll();
                });
            },
            { passive: true }
        );
    }, 100);

    private handleDragStart() {
        this.isDragging = true;
        this.host.style.cursor = "grabbing";
        this.eventMouseMove = this.handleDragMove.bind(this);
        this.eventMouseUp = this.handleDragEnd.bind(this);
        document.addEventListener("mousemove", this.eventMouseMove);
        document.addEventListener("mouseup", this.eventMouseUp);
    }
    private handleDragMove(e) {
        if (!this.isDragging) return;
        const scrollRelation = e.movementX / this.host.clientWidth;
        this.scrollElement.scrollLeft =
            this.scrollElement.scrollLeft + scrollRelation * this.scrollWidth;
    }
    private handleDragEnd() {
        this.isDragging = false;
        this.host.style.cursor = "auto";
        document.removeEventListener("mousemove", this.eventMouseMove);
        document.removeEventListener("mouseup", this.eventMouseUp);
    }

    private getScrollBar() {
        return (
            <div
                class={`${baseClass}__track`}
                style={{
                    marginLeft: `${this.scroll * 100}%`,
                    width: `${this.width * 100}%`,
                    display: this.width >= 1 ? "none" : undefined,
                }}
                onMouseDown={this.handleDragStart.bind(this)}
            />
        );
    }

    render() {
        return (
            <Host class={baseClass}>
                {(this.scrollbarPlacement === "top" ||
                    this.scrollbarPlacement === "top-bottom") &&
                    this.getScrollBar()}
                <div class={`${baseClass}__scroll`}>
                    <slot />
                </div>
                {(this.scrollbarPlacement === "bottom" ||
                    this.scrollbarPlacement === "top-bottom") &&
                    this.getScrollBar()}
            </Host>
        );
    }
}
